import { useMemo } from 'react';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { VEHICLE_TYPES, VEHICLE_TYPE_LABELS } from '../types/warranty.types';

const VEHICLE_TYPE_STRING_TO_NUMBER: Record<string, number> = {
  STANDARD: VEHICLE_TYPES.STANDARD,
  COLLECTION: VEHICLE_TYPES.COLLECTION,
  TWO_WHEELS: VEHICLE_TYPES.TWO_WHEELER,
};

/**
 * Hook qui retourne les types de véhicules autorisés par la configuration de la société.
 * - Si la configuration est vide ou non disponible, tous les types sont autorisés.
 * - Si un seul type est autorisé, `autoVehicleType` contient la valeur pour l'auto-sélection.
 */
export const useAllowedVehicleTypes = (societyId: string | undefined) => {
  const { data: config } = useEffectiveConfiguration(societyId);

  return useMemo(() => {
    const allTypes = Object.keys(VEHICLE_TYPE_LABELS).map(Number);

    if (!config?.vehicleTypes || config.vehicleTypes.length === 0) {
      return {
        allowedVehicleTypes: allTypes,
        autoVehicleType: undefined as number | undefined,
        isSingleType: false,
      };
    }

    const allowedTypes = config.vehicleTypes
      .map((type) => VEHICLE_TYPE_STRING_TO_NUMBER[type])
      .filter((type): type is number => type !== undefined);

    return {
      allowedVehicleTypes: allowedTypes,
      autoVehicleType: allowedTypes.length === 1 ? allowedTypes[0] : undefined,
      isSingleType: allowedTypes.length === 1,
    };
  }, [config?.vehicleTypes]);
};
